import { LockFill } from '@gravity-ui/icons';
import { AgreeToTermsPolicy, type SignInIdentifier } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import WebAuthnContext from '@/Providers/WebAuthnContextProvider/WebAuthnContext';
import { SmartInputField, PasswordInputField } from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import ForgotPasswordLink from '@/containers/ForgotPasswordLink';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useFocusFirstInvalidField from '@/hooks/use-focus-first-invalid-field';
import usePasswordSignIn from '@/hooks/use-password-sign-in';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import { useForgotPasswordSettings } from '@/hooks/use-sie';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly signInMethods: SignInIdentifier[];
};

export type FormState = {
  identifier: IdentifierInputValue;
  password: string;
};

const PasswordSignInForm = ({ className, autoFocus, signInMethods }: Props) => {
  const { t } = useTranslation();

  const { errorMessage, clearErrorMessage, onSubmit } = usePasswordSignIn();
  const { isForgotPasswordEnabled } = useForgotPasswordSettings();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { isPasskeyFlowProcessing } = useContext(WebAuthnContext);
  const prefilledIdentifier = usePrefilledIdentifier({ enabledIdentifiers: signInMethods });

  const {
    watch,
    register,
    trigger,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: {
      identifier: prefilledIdentifier,
      password: '',
    },
  });

  const formRef = useFocusFirstInvalidField(isSubmitting);

  // The terms modal awaits inside the submit, so without this the button spins behind it.
  const [isConfirmingTerms, setIsConfirmingTerms] = useState(false);

  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(
    watch('identifier')
  );

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      if (isPasskeyFlowProcessing) {
        return;
      }

      clearErrorMessage();

      await handleSubmit(async ({ identifier: { type, value }, password }) => {
        if (!type) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }

        // Check if the user has agreed to the terms and privacy policy before signing in when the policy is set to `Manual`
        if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual) {
          setIsConfirmingTerms(true);
          const hasAgreed = await termsValidation();
          setIsConfirmingTerms(false);

          if (!hasAgreed) {
            return;
          }
        }

        await onSubmit({
          identifier: { type, value },
          password,
        });
      })(event);
    },
    [
      agreeToTermsPolicy,
      clearErrorMessage,
      handleSubmit,
      navigateToSingleSignOn,
      onSubmit,
      setIdentifierInputValue,
      showSingleSignOnForm,
      termsValidation,
      isPasskeyFlowProcessing,
    ]
  );

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  return (
    <form
      ref={formRef}
      className={classNames('flex flex-col', className)}
      onSubmit={onSubmitHandler}
    >
      <div className="flex flex-col gap-4">
        <Controller
          control={control}
          name="identifier"
          rules={{
            validate: ({ type, value }) => {
              if (!type || !value) {
                return getGeneralIdentifierErrorMessage(signInMethods, 'required');
              }

              const errorMessage = validateIdentifierField(type, value);

              return errorMessage
                ? getGeneralIdentifierErrorMessage(signInMethods, 'invalid')
                : true;
            },
            onChange: () => {
              if (errors.identifier) {
                clearErrors('identifier');
              }
            },
          }}
          render={({ field, formState: { defaultValues } }) => (
            <SmartInputField
              autoFocus={autoFocus}
              {...field}
              isDanger={!!errors.identifier}
              errorMessage={errors.identifier?.message}
              enabledTypes={signInMethods}
              defaultValue={defaultValues?.identifier?.value}
            />
          )}
        />
        {showSingleSignOnForm && (
          <p className="text-sm text-muted">{t('description.single_sign_on_enabled')}</p>
        )}

        {!showSingleSignOnForm && (
          <PasswordInputField
            autoComplete="current-password"
            label={t('input.password')}
            // A server rejection is reported below in `errorMessage`, but it belongs to this
            // field: marking it danger sets `aria-invalid` and gives focus-first-invalid a target.
            isDanger={!!errors.password || !!errorMessage}
            errorMessage={errors.password?.message}
            {...register('password', {
              required: t('error.password_required'),
              onChange: () => {
                void trigger('password');
              },
            })}
          />
        )}

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

        {isForgotPasswordEnabled && !showSingleSignOnForm && (
          <ForgotPasswordLink
            className="self-start"
            identifier={watch('identifier').type}
            value={watch('identifier').value}
          />
        )}

        {/*
         * Hidden with CSS rather than unmounted: unmounting re-renders the form, which re-runs
         * autofill, which pins the SSO form open for an SSO-enabled autofilled value.
         */}
        <TermsAndPrivacyCheckbox
          className={classNames(agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && 'hidden')}
        />

        <CaptchaBox />
      </div>

      <Button
        name="submit"
        className="mt-6"
        title={showSingleSignOnForm ? 'action.single_sign_on' : 'action.sign_in'}
        icon={showSingleSignOnForm ? <LockFill /> : undefined}
        htmlType="submit"
        isLoading={(isSubmitting && !isConfirmingTerms) || isPasskeyFlowProcessing}
      />

      <input hidden type="submit" />
    </form>
  );
};

export default PasswordSignInForm;
